import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import {
  ETH_BPS_HISTORY_SAMPLE_MS,
  ethBpsRangeToMs,
  type EthBpsRange,
} from "../constants/ethBpsChart";
import { OCPP_TRAFFIC_RANGES } from "../constants/ocppTrafficChart";
import { isUiLoadReduced, subscribeUiLoad } from "../lib/uiLoadStore";
import { useEffectiveChartNow } from "./useChartPause";

export type EthBpsPoint = {
  t: number;
  rx_Mbps: number;
  tx_Mbps: number;
};

export type EthBpsDbPoint = {
  ts_ms: number;
  rx_Mbps: number;
  tx_Mbps: number;
};

type Listener = () => void;

/** Latest live (~100ms) sample for sparkline only — chart history is 1s SQLite. */
let latestLive: EthBpsPoint | null = null;
let liveVersion = 0;
const liveListeners = new Set<Listener>();

function emitLive() {
  liveVersion += 1;
  for (const l of liveListeners) l();
}

export function pushEthBpsSample(sample: {
  t?: string;
  rx_Mbps?: number;
  tx_Mbps?: number;
}) {
  const ts = sample.t ? Date.parse(sample.t) : Date.now();
  if (!Number.isFinite(ts)) return;
  const rx = Number(sample.rx_Mbps);
  const tx = Number(sample.tx_Mbps);
  if (!Number.isFinite(rx) || !Number.isFinite(tx)) return;
  if (rx > 2000 || tx > 2000 || rx < 0 || tx < 0) return;
  latestLive = { t: ts, rx_Mbps: rx, tx_Mbps: tx };
  emitLive();
}

export function getLatestEthBps(): EthBpsPoint | null {
  return latestLive;
}

export function subscribeEthBps(listener: Listener) {
  liveListeners.add(listener);
  return () => {
    liveListeners.delete(listener);
  };
}

/** 1s integrated history from SQLite (byte-delta average Mbps per second). */
export function useEthBpsSeries(
  range: EthBpsRange,
  options?: { paused?: boolean; nowMs?: number },
) {
  const userPaused = options?.paused ?? false;
  const nowMs = options?.nowMs ?? Date.now();
  const loadReduced = useSyncExternalStore(subscribeUiLoad, isUiLoadReduced, isUiLoadReduced);
  const liveVer = useSyncExternalStore(subscribeEthBps, () => liveVersion, () => liveVersion);
  void liveVer;

  const [dbPoints, setDbPoints] = useState<EthBpsDbPoint[]>([]);
  const [resolution, setResolution] = useState<string>("raw_1s");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const windowMs = ethBpsRangeToMs(range);
  const { historicalMode, chartFrozen, effectiveNowMs } = useEffectiveChartNow(
    userPaused,
    nowMs,
    windowMs,
  );
  const dataPaused = chartFrozen || loadReduced;

  const fetchHistory = useCallback(
    async (opts?: { silent?: boolean }) => {
      const silent = opts?.silent ?? false;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const res = await fetch(`/api/ocpp/eth-bps/history?range=${range}`);
        if (!res.ok) throw new Error(`history ${res.status}`);
        const body = (await res.json()) as {
          points?: EthBpsDbPoint[];
          resolution?: string;
        };
        setDbPoints(body.points ?? []);
        setResolution(body.resolution ?? "raw_1s");
      } catch (err) {
        if (!silent) {
          setError(err instanceof Error ? err.message : "Failed to load eth history");
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [range],
  );

  useEffect(() => {
    if (loadReduced) return;
    void fetchHistory();
  }, [fetchHistory, loadReduced]);

  useEffect(() => {
    if (dataPaused) return;
    const id = window.setInterval(() => {
      void fetchHistory({ silent: true });
    }, ETH_BPS_HISTORY_SAMPLE_MS);
    return () => window.clearInterval(id);
  }, [fetchHistory, dataPaused]);

  useEffect(() => {
    if (dataPaused) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void fetchHistory({ silent: true });
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchHistory, dataPaused]);

  const windowFromMs = windowMs == null ? 0 : effectiveNowMs - windowMs;

  const series = useMemo(() => {
    const points = dbPoints.filter((p) => p.ts_ms >= windowFromMs);
    return {
      x: points.map((p) => new Date(p.ts_ms).toISOString()),
      yRx: points.map((p) => p.rx_Mbps),
      yTx: points.map((p) => p.tx_Mbps),
    };
  }, [dbPoints, windowFromMs]);

  const xRange = useMemo((): [string, string] => {
    const end = effectiveNowMs;
    if (windowMs == null) {
      if (series.x.length >= 2) {
        return [series.x[0]!, series.x[series.x.length - 1]!];
      }
      return [new Date(end - 60_000).toISOString(), new Date(end).toISOString()];
    }
    return [new Date(end - windowMs).toISOString(), new Date(end).toISOString()];
  }, [windowMs, effectiveNowMs, series.x]);

  return {
    x: series.x,
    yRx: series.yRx,
    yTx: series.yTx,
    xRange,
    latest: latestLive,
    count: series.x.length,
    loading,
    error,
    ranges: OCPP_TRAFFIC_RANGES,
    fetchHistory,
    historicalMode,
    resolution,
  };
}
